// Registers the service worker and reports when one is waiting.
//
// Deliberately absent: any automatic reload. The classic
//   navigator.serviceWorker.addEventListener('controllerchange', () => location.reload())
// reloads EVERY open window, including one mid-payroll. Activation is
// requested by a person, through the guard, and reloads only the window that
// asked.
import { subscribeToHolds, currentHolds, summarise } from './dirtyRegistry';
import { CHANNEL, POLL_TIMEOUT_MS, type TabReply } from './tabConsensus';
import { startUpdateChecks } from './updateChecks';
import { awaitController } from './awaitController';
import { decideRegistration, parsePilotTenants } from './registrationPolicy';
import { clearAttempts } from './recoveryState';

/** Registration is idempotent; the policy may ask for it more than once. */
let registered = false;

const TAB_ID = Math.random().toString(36).slice(2);

let waitingWorker: ServiceWorker | null = null;
const waitingListeners = new Set<() => void>();

export const getWaitingWorker = () => waitingWorker;
export const onWaitingChange = (fn: () => void) => {
  waitingListeners.add(fn);
  return () => { waitingListeners.delete(fn); };
};
const announceWaiting = (w: ServiceWorker | null) => {
  waitingWorker = w;
  waitingListeners.forEach((f) => f());
};

/** Answers other tabs asking what we would lose. Always listening, so a tab
 *  can be polled whether or not it is the one updating. */
const listenForPolls = () => {
  if (typeof BroadcastChannel === 'undefined') return;
  const ch = new BroadcastChannel(CHANNEL);
  ch.addEventListener('message', (e) => {
    if (e.data?.type !== 'POLL') return;
    const s = summarise(currentHolds());
    const reply: TabReply = { tabId: TAB_ID, unsaved: s.unsaved, pending: s.pending, labels: s.labels };
    ch.postMessage({ type: 'POLL_REPLY', pollId: e.data.pollId, reply });
  });
  // Re-announce on change so a long poll sees the latest state.
  subscribeToHolds(() => { /* state is read at reply time; nothing to push */ });
};

export const pollOtherTabs = (expected: number): Promise<{ replies: TabReply[]; elapsedAll: boolean }> =>
  new Promise((resolve) => {
    if (typeof BroadcastChannel === 'undefined') {
      // No way to ask, so speak only for ourselves and let the expected count
      // decide. decideConsensus treats a shortfall as a blocker.
      const s = summarise(currentHolds());
      resolve({ replies: [{ tabId: TAB_ID, unsaved: s.unsaved, pending: s.pending, labels: s.labels }], elapsedAll: true });
      return;
    }
    const pollId = Math.random().toString(36).slice(2);
    const ch = new BroadcastChannel(CHANNEL);
    const replies: TabReply[] = [];
    // This tab answers for itself without a round trip.
    const mine = summarise(currentHolds());
    replies.push({ tabId: TAB_ID, unsaved: mine.unsaved, pending: mine.pending, labels: mine.labels });

    ch.addEventListener('message', (e) => {
      if (e.data?.type === 'POLL_REPLY' && e.data.pollId === pollId) {
        if (e.data.reply.tabId !== TAB_ID) replies.push(e.data.reply);
      }
    });
    ch.postMessage({ type: 'POLL', pollId });
    setTimeout(() => { ch.close(); resolve({ replies, elapsedAll: true }); }, POLL_TIMEOUT_MS);
  });

/** Asks the worker how many windows exist. Only it can enumerate them. */
export const countClients = async (): Promise<number> => {
  const reg = await navigator.serviceWorker?.getRegistration();
  const target = reg?.active;
  if (!target) return 1;
  return new Promise((resolve) => {
    const ch = new MessageChannel();
    const timer = setTimeout(() => resolve(1), 500);
    ch.port1.onmessage = (e) => { clearTimeout(timer); resolve(e.data?.count ?? 1); };
    target.postMessage({ type: 'COUNT_CLIENTS' }, [ch.port2]);
  });
};

/**
 * Asks the waiting worker to take over, and resolves only once it actually
 * controls this page — or once we give up waiting.
 *
 * Returning as soon as the message was posted was a race, and it lost: the
 * caller reloaded immediately, the page came back on the OLD worker, and the
 * new one stayed `installed` forever while the prompt kept reappearing.
 */
export const activateWaiting = async (): Promise<void> => {
  const worker = waitingWorker;
  if (!worker) return;
  await awaitController({
    onControllerChange: (fn) => {
      navigator.serviceWorker.addEventListener('controllerchange', fn);
      return () => navigator.serviceWorker.removeEventListener('controllerchange', fn);
    },
    postSkipWaiting: () => worker.postMessage({ type: 'SKIP_WAITING' }),
    setTimer: (fn, ms) => { const id = window.setTimeout(fn, ms); return () => window.clearTimeout(id); },
  });
  // A timeout still falls through to the caller's reload. It is the best
  // remaining move, and the reload-loop guard is what notices if it keeps
  // not helping — which is exactly how this bug surfaced.
};

/**
 * Takes away a worker this browser should no longer have, and the caches
 * with it. Reloads nothing: open pages simply stop being controlled, and
 * recovery must never cost anyone their work.
 */
const removeServiceWorker = async (): Promise<void> => {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    const names = await caches.keys();
    await Promise.all(names.filter((n) => n.startsWith('kp-sw-')).map((n) => caches.delete(n)));
  } catch { /* private mode, quota — nothing useful to do, and it must not throw */ }
};

/**
 * Applies the pilot policy. Called at startup and again whenever the
 * workspace changes, because the tenant is unknown until someone signs in.
 *
 * ⚠️ This is NOT the whole kill switch. It can only act on a browser that
 * comes back and signs in. A browser that never returns keeps whatever it
 * has, which is what the server-side tombstone (KP_SW_KILL=1) is for.
 */
export const syncServiceWorker = async (tenantSlug: string | null): Promise<void> => {
  if (!('serviceWorker' in navigator)) return;
  const action = decideRegistration({
    pilotTenants: parsePilotTenants(import.meta.env.VITE_PWA_PILOT_TENANTS),
    tenantSlug,
    // Registrations OR leftover caches — see registrationPolicy.ts for why
    // a cache can outlive the worker that made it.
    carriesWorkerOrCaches:
      (await navigator.serviceWorker.getRegistrations()).length > 0
      || (await caches.keys().catch(() => [])).some((n) => n.startsWith('kp-sw-')),
  });
  trace.slug = tenantSlug;
  trace.decision = action;
  if (action === 'register') registerServiceWorker();
  else if (action === 'remove') await removeServiceWorker();
};

/**
 * Why the last registration attempt failed, or null.
 *
 * Exists because the catch below used to discard this. "No worker" and "a
 * worker that refused to install" look identical from the outside and need
 * completely different fixes.
 */
let lastRegistrationError: string | null = null;

/** The last registration failure, for UI that must explain itself. */
export const registrationError = (): string | null => lastRegistrationError;

/**
 * What the pilot gate actually did, at normal startup.
 *
 * ⚠️ Exists because "zero registrations" has at least four causes that look
 * identical from outside: the gate never ran, it saw no workspace, it decided
 * this workspace was out, or register() itself failed. Production spent an
 * evening unable to tell them apart, because the only record — the error from
 * register() — was discarded by an empty catch.
 *
 * Carries no personal data: a workspace slug, a decision, and an error string.
 */
export interface RegistrationTrace {
  /** The workspace the gate resolved, or null if it could not. */
  slug: string | null;
  /** What the policy decided for that workspace. */
  decision: string | null;
  /** Whether navigator.serviceWorker.register() was actually called. */
  attempted: boolean;
  /** 'registered <scope>', or 'failed: Name - message', or null if not attempted. */
  outcome: string | null;
}

const trace: RegistrationTrace = { slug: null, decision: null, attempted: false, outcome: null };

/** A snapshot of what the gate did. Copied, so a caller cannot corrupt it. */
export const registrationDiagnostics = (): RegistrationTrace => ({ ...trace });

export const registerServiceWorker = (): void => {
  if (!('serviceWorker' in navigator)) return;
  if (registered) return; // idempotent: syncServiceWorker runs on every tenant change
  registered = true;
  listenForPolls();
  const start = () => {
    trace.attempted = true;
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      trace.outcome = `registered ${reg.scope}`;
      if (reg.waiting) announceWaiting(reg.waiting);
      // Without this the prompt almost never fires for the people it is for:
      // the browser only re-fetches sw.js on a navigation in scope, so a
      // window left open — an installed app's normal state — never learns a
      // release shipped. See updateChecks.ts.
      startUpdateChecks({
        update: () => reg.update(),
        now: () => Date.now(),
        setInterval: (fn, ms) => { const id = window.setInterval(fn, ms); return () => window.clearInterval(id); },
        onVisible: (fn) => {
          const h = () => { if (document.visibilityState === 'visible') fn(); };
          document.addEventListener('visibilitychange', h);
          return () => document.removeEventListener('visibilitychange', h);
        },
        isVisible: () => document.visibilityState === 'visible',
      });
      reg.addEventListener('updatefound', () => {
        const next = reg.installing;
        next?.addEventListener('statechange', () => {
          // "installed" with a controller already present means an update is
          // waiting; without one it is the first install and there is
          // nothing to tell anybody.
          if (next.state === 'installed' && navigator.serviceWorker.controller) {
            // ⚠️ A NEW release deserves a fresh start. Failures against the
            // previous one say nothing about this one, and carrying them
            // over would pause an update that might work perfectly.
            //
            // Deliberately only here, in the updatefound path — not in the
            // `reg.waiting` check at load, which fires for a worker that was
            // already waiting and would reset the counter on every page load,
            // leaving no counter at all.
            clearAttempts('worker-waiting');
            announceWaiting(next);
          }
        });
      });
    }).catch((e: unknown) => {
      // Must never break the app — but swallowing it ENTIRELY meant a browser
      // that could not register was indistinguishable from one that was never
      // asked to. In production on 2026-10-05 a tenant that WAS in the pilot
      // had zero registrations and there was nothing anywhere saying why.
      // Recorded so the desktop-notifications panel can say something true.
      lastRegistrationError = (e as Error)?.message || 'Service worker registration failed.';
      trace.outcome = `failed: ${(e as Error)?.name || 'Error'} - ${lastRegistrationError}`;
    });
  };
  // 'load' has usually already fired by the time a tenant is known, and a
  // listener added afterwards never runs — which would silently register
  // nothing for every pilot user.
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
};
